//Constants
import { Colors } from "@ms/uno-constants/lib/local/Colors";
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
import { PlannerBorderRadius } from "../../../../constants/StyleConstants";
import { DialogWithRightPanelMaxWidth, DialogWithRightPanelMinWidth, DialogWithRightPanelWidth, MainDialogMaxWidth, MainDialogMinWidth, MainDialogWidth, TaskEditorMaxDialogHeight, TaskEditorSharedDialogHeight, TaskEditorTodoDialogHeight } from "@ms/uno-constants/lib/local/ModalConstants";
// Styles
import { concatStyleSets, FontSizes, memoizeFunction, mergeStyles } from "@fluentui/react";
import { highContrastBorders } from "../../../../utilities/HighContrast.styles";
import { backgroundColorNeutralPrimary, dialogBackgroundPrimary, flexChildSizeFixed, flexParent, fontColorNeutralPrimary, getZIndex, styleScrollbar, themedButtonStyle } from "../../../../utilities/Mixin.styles";
/**
 * Helper to get styles for TaskEditor
 * @param theme Planner theme
 * @param customStyles Any custom style overrides
 */ export const getStyles = memoizeFunction((theme, shouldSetPrivateDialogHeight, isRightPaneOpen, isFullScreenMode, customStyles)=>{
    const { name: themeName } = theme;
    const height = shouldSetPrivateDialogHeight ? TaskEditorTodoDialogHeight : TaskEditorSharedDialogHeight;
    const TaskEditorDialogStyles = {
        root: [
            {
                ".ms-Overlay": [
                    (themeName === PlannerTheme.Light || themeName === PlannerTheme.Dark || themeName === PlannerTheme.HighContrast) && {
                        backgroundColor: "transparent"
                    }
                ]
            }
        ],
        container: [
            fontColorNeutralPrimary(theme),
            dialogBackgroundPrimary(theme),
            highContrastBorders(),
            {
                height: height,
                maxHeight: TaskEditorMaxDialogHeight,
                overflow: "hidden",
                alignItems: "center",
                width: isRightPaneOpen ? DialogWithRightPanelWidth : MainDialogWidth,
                maxWidth: isRightPaneOpen ? DialogWithRightPanelMaxWidth : MainDialogMaxWidth,
                minWidth: isRightPaneOpen ? DialogWithRightPanelMinWidth : MainDialogMinWidth,
                borderRadius: PlannerBorderRadius,
                boxShadow: "rgba(0, 0, 0, 0.4) 0px 0px 5px 0px",
                ".ms-Modal-scrollableContent": {
                    height: "100%"
                }
            },
            themeName === PlannerTheme.HighContrast && {
                backgroundColor: Colors.Black,
                borderColor: Colors.White,
                borderWidth: "1px",
                borderStyle: "solid"
            }
        ],
        scrollable: [
            styleScrollbar(theme),
            {
                overflowY: "auto",
                maxHeight: height,
                [`@media (max-height: ${height})`]: {
                    maxHeight: TaskEditorMaxDialogHeight
                }
            }
        ],
        header: {
            position: "relative",
            width: "100%",
            boxSizing: "border-box"
        },
        title: {
            visibility: "collapse",
            margin: "0",
            fontSize: FontSizes.xLarge,
            padding: "32px 36px 20px 28px"
        },
        topButtonContainer: [
            flexParent(),
            {
                position: "absolute",
                top: "0",
                padding: "12px 12px 0 0",
                right: "0",
                "> *": flexChildSizeFixed(),
                "> .ms-Button": [
                    fontColorNeutralPrimary(theme),
                    {
                        ":hover": {
                            backgroundColor: "transparent"
                        }
                    }
                ]
            }
        ],
        content: {
            padding: "0 28px 20px"
        },
        suggestionsFloatingButton: [
            themedButtonStyle(theme),
            {
                position: "absolute",
                zIndex: "1",
                bottom: "32px",
                right: "24px",
                fontSize: "13px",
                height: "36px",
                ".suggestionsButtonText": {
                    marginLeft: "8px"
                }
            }
        ],
        // right panel container for task chat
        taskChatPanelContainer: [
            flexParent("column"),
            {
                flex: "1 0 350px",
                maxWidth: "350px"
            }
        ],
        // left panel container for task editor
        taskPanelContainer: [
            flexParent(),
            {
                height: "100%"
            }
        ]
    };
    const fullScreenStyles = {
        taskPanelContainer: [
            backgroundColorNeutralPrimary(theme),
            {
                width: "100%",
                height: "100%",
                position: "absolute",
                top: "0",
                zIndex: getZIndex("taskEditorFullMode")
            }
        ],
        scrollable: [
            {
                overflowY: "auto",
                maxHeight: "unset",
                maxWidth: "unset",
                flexGrow: 2
            }
        ],
        content: {
            marginTop: "40px"
        },
        topButtonContainer: {
            boxSizing: "border-box",
            padding: "42px",
            width: "100%",
            "> *:first-child": {
                marginRight: "auto"
            }
        },
        taskChatPanelContainer: {
            maxWidth: "800px",
            flex: "1 1 800px",
            width: "unset"
        }
    };
    return concatStyleSets(TaskEditorDialogStyles, isFullScreenMode && fullScreenStyles, customStyles);
});
/**
 * Get the classnames associated with the computed styles
 * @param styles The styles for the component
 */ export const getClassNames = memoizeFunction((styles)=>({
        root: mergeStyles("taskEditor-dialog", styles.root),
        container: mergeStyles("taskEditor-dialog-container", styles.container),
        scrollable: mergeStyles("taskEditor-dialog-scrollable", styles.scrollable),
        header: mergeStyles("taskEditor-dialog-header", styles.header),
        title: mergeStyles("taskEditor-dialog-title", styles.title),
        topButtonContainer: mergeStyles("taskEditor-dialog-topButton", styles.topButtonContainer),
        content: mergeStyles("taskEditor-dialog-content", styles.content),
        suggestionsFloatingButton: mergeStyles("suggestionsFloatingButton", styles.suggestionsFloatingButton),
        taskChatPanelContainer: mergeStyles("taskChatPanel", styles.taskChatPanelContainer),
        taskPanelContainer: mergeStyles("taskPanelContainer", styles.taskPanelContainer)
    }));
